import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `http://localhost:4000${url}`;
};

const statusStyle = (s) => ({
  pending:          'bg-amber-100 text-amber-700',
  confirmed:        'bg-blue-100 text-blue-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  completed:        'bg-emerald-100 text-emerald-700',
  cancelled:        'bg-red-100 text-red-600',
}[s] || 'bg-gray-100 text-gray-600');

export default function MyShopOrdersPanel() {
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [deliveryForm, setDeliveryForm] = useState({});
  const [assigning,    setAssigning]    = useState({});

  const handleAssignDelivery = async (orderId) => {
    const form = deliveryForm[orderId] || {};
    if (!form.name || !form.phone) { alert('Enter delivery boy name and phone'); return; }
    setAssigning(prev => ({ ...prev, [orderId]: true }));
    try {
      await api.patch(`/api/butcher/shop-orders/${orderId}/assign-delivery`, {
        delivery_boy_name: form.name,
        delivery_boy_phone: form.phone
      });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'out_for_delivery' } : o));
    } catch (err) {
      alert('Failed to assign delivery boy');
    } finally {
      setAssigning(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/butcher/shop-orders');
      setOrders(data);
    } catch (err) {
      console.error('shop orders error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await api.patch(`/api/butcher/shop-orders/${orderId}/status`,
        { status }
      );
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const [filter, setFilter] = useState('all');

  const counts = {
    total:     orders.length,
    pending:   orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  const filtered =
    filter === 'pending'   ? orders.filter(o => o.status === 'pending')
    : filter === 'confirmed' ? orders.filter(o => o.status === 'confirmed')
    : filter === 'completed' ? orders.filter(o => o.status === 'completed')
    : orders;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-black text-on-surface">Shop Orders</h2>
        <p className="text-sm text-secondary mt-1">
          Orders placed by customers on your meat products.
        </p>
      </div>

      {/* Stats */}
      <div className="flex gap-3 flex-wrap">
        {[
          { key: 'all',       label: 'Total',     val: counts.total,     color: 'bg-white border border-outline-variant/20 text-secondary' },
          { key: 'pending',   label: 'Pending',   val: counts.pending,   color: 'bg-amber-50 text-amber-700'    },
          { key: 'confirmed', label: 'Confirmed', val: counts.confirmed, color: 'bg-blue-50 text-blue-700'      },
          { key: 'completed', label: 'Completed', val: counts.completed, color: 'bg-emerald-50 text-emerald-700' },
          ].map(p => (
            <button key={p.label} onClick={() => setFilter(p.key)}
              className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition-all
                ${filter === p.key ? 'ring-2 ring-offset-1 ring-primary scale-105' : ''} ${p.color}`}>
            {p.val} {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center shadow-sm border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-outline-variant">storefront</span>
          <p className="mt-3 font-bold text-on-surface">No shop orders yet</p>
          <p className="text-xs text-secondary mt-1">
            When customers buy your meat products, orders appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <div key={o.id}
              className="bg-white rounded-2xl shadow-sm border border-outline-variant/10
                         overflow-hidden hover:border-primary/20 transition-all">

              {/* Top row */}
              <div className="flex items-center gap-4 p-4">
                {o.image_url
                  ? <img src={getImageUrl(o.image_url)} alt={o.product_name}
                         className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                  : <div className="w-16 h-16 rounded-xl bg-surface-container
                                    flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-2xl text-outline-variant">
                        storefront
                      </span>
                    </div>
                }

                <div className="flex-1 min-w-0">
                  <p className="font-bold text-on-surface">{o.product_name}</p>
                  <p className="text-[11px] text-secondary font-mono mt-0.5">
                    Order #{o.id} · {new Date(o.created_at).toLocaleDateString('en-IN')}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center
                                    justify-center text-primary font-black text-[10px]">
                      {(o.buyer_name || 'B')[0].toUpperCase()}
                    </div>
                    <span className="text-[11px] text-secondary">{o.buyer_name || '—'}</span>
                    {o.buyer_phone && (
                      <a href={`tel:${o.buyer_phone}`}
                         className="text-[11px] text-primary hover:underline">
                        {o.buyer_phone}
                      </a>
                    )}
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <p className="font-black text-primary text-lg">
                    ₹{Number(o.amount || 0).toLocaleString('en-IN')}
                  </p>
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full
                                    ${statusStyle(o.status)}`}>
                    {o.status}
                  </span>
                </div>

                <button onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                  className="ml-2 p-2 rounded-xl hover:bg-surface-container
                             text-secondary transition-all flex-shrink-0">
                  <span className="material-symbols-outlined text-[20px]">
                    {expanded === o.id ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
              </div>

              {/* Expanded */}
              {expanded === o.id && (
                <div className="border-t border-outline-variant/10 p-4 space-y-4
                                bg-surface-container-low/40">

                  {/* Delivery info */}
                  <div className="bg-white rounded-xl p-4 border border-outline-variant/10">
                    <p className="text-[10px] font-black uppercase tracking-widest
                                  text-secondary mb-2">Delivery Address</p>
                    <p className="text-sm text-secondary">
                      {o.delivery_address || 'Not provided'}
                    </p>
                    {o.note && (
                      <p className="text-xs italic text-outline mt-2">"{o.note}"</p>
                    )}
                  </div>

                  {/* Actions */}
                  {o.status === 'pending' && (
                    <div className="flex gap-3">
                      <button onClick={() => handleUpdateStatus(o.id, 'confirmed')}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5
                                   rounded-xl bg-emerald-600 text-white font-bold text-sm
                                   hover:bg-emerald-700 transition-all">
                        <span className="material-symbols-outlined text-[18px]">check_circle</span>
                        Accept Order
                      </button>
                      <button onClick={() => handleUpdateStatus(o.id, 'cancelled')}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5
                                   rounded-xl bg-red-50 text-red-600 font-bold text-sm
                                   border border-red-100 hover:bg-red-600 hover:text-white
                                   transition-all">
                        <span className="material-symbols-outlined text-[18px]">cancel</span>
                        Reject
                      </button>
                    </div>
                  )}

                  {o.status === 'confirmed' && (
                  <div className="space-y-3">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-secondary">Assign Delivery Boy</p>
                    <div className="flex gap-2">
                      <input placeholder="Delivery boy name"
                        value={deliveryForm[o.id]?.name || ''}
                        onChange={e => setDeliveryForm(prev => ({ ...prev, [o.id]: { ...prev[o.id], name: e.target.value } }))}
                        className="flex-1 border border-outline-variant/30 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                      <input placeholder="Phone number"
                        value={deliveryForm[o.id]?.phone || ''}
                        onChange={e => setDeliveryForm(prev => ({ ...prev, [o.id]: { ...prev[o.id], phone: e.target.value } }))}
                        className="flex-1 border border-outline-variant/30 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                    </div>
                    <button onClick={() => handleAssignDelivery(o.id)} disabled={assigning[o.id]}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50">
                      <span className="material-symbols-outlined text-[18px]">local_shipping</span>
                      {assigning[o.id] ? 'Assigning...' : 'Assign & Dispatch'}
                    </button>
                  </div>
                )}

                  {o.status === 'out_for_delivery' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-purple-50 border border-purple-100">
                      <span className="material-symbols-outlined text-[18px] text-purple-600">delivery_dining</span>
                      <span className="text-sm font-medium text-purple-700">{o.delivery_boy_name} · {o.delivery_boy_phone}</span>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-3">
                      <span className="material-symbols-outlined text-amber-500 text-[22px]">info</span>
                      <div>
                        <p className="text-sm font-bold text-amber-800">Waiting for delivery confirmation</p>
                        <p className="text-[11px] text-amber-600">The delivery driver will confirm via OTP on delivery</p>
                      </div>
                    </div>
                    {o.payment_method === 'cod' && (
                      <button onClick={async () => {
                        await fetch(`/api/butcher/shop-orders/${o.id}/cash-received`, {
                          method: 'PATCH', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        });
                        setOrders(prev => prev.map(x => x.id === o.id ? { ...x, payment_status: 'paid' } : x));
                      }}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition-all">
                        <span className="material-symbols-outlined text-[18px]">payments</span>
                        Mark Cash Received
                      </button>
                    )}
                  </div>
                )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}